import {useApplicationContext} from '@gaas/gaas-react-component-library';
import TollAccountType from '../types/tollAccountType';
import TollProvider from '../types/TollProvider';
import {ProductData} from '../context/ApplicationContext';
import {ApplicationState, RegistrationForm} from '../types/GaasAppState';
import {convertToGaasRegistrationForm} from '../services/gaas-submission-data-service';
import config from '../config';

export interface RegistrationProductData {
    lpn: string,
    tollAccountType: TollAccountType,
    tollProvider: TollProvider,
    tollAccountNumber: string,
    tollAccountEmail: string,
    abn: string | null,
    businessName: string | null,
    isRideShareOrTaxi: boolean | null,
}

interface UseTrRegistrationForm {
    isValid: (productData: ProductData) => boolean,
    isValidTollAccountData: (productData: ProductData) => boolean,
    isValidBusinessDetailsData: (productData: ProductData) => boolean,
    data: (productData: ProductData) => RegistrationForm,
}

/**
 * The structure used in calling /apply
 */
const composeRegistrationForm = (globalState: ApplicationState, productData: ProductData): RegistrationForm => {
    const registrationForm: RegistrationForm = convertToGaasRegistrationForm(globalState, config);
    const submissionData = registrationForm.submissionData;
    const trProductData: RegistrationProductData = {
        tollAccountType: productData.tollAccountType!,
        lpn: productData.tollAccountDetailsPageData!.lpn.trim(),
        tollProvider: productData.tollAccountDetailsPageData!.tollProvider,
        tollAccountNumber: productData.tollAccountDetailsPageData!.tollAccountNumber.trim(),
        tollAccountEmail: productData.tollAccountDetailsPageData!.tollAccountEmail.trim(),
        abn: productData.soleTraderDetailData?.abn?.trim() ?? null,
        businessName: productData.soleTraderDetailData?.businessName?.trim() ?? null,
        isRideShareOrTaxi: productData.soleTraderDetailData?.isRideShareOrTaxi ?? null,
    };

    return {
        ...registrationForm,
        submissionData: {
            ...submissionData,
            productData: {
                [config.productCode]: trProductData,
            },
            declarations: {
                // Keep these declaration keys in sync with `TollReliefDeclarations` in backend
                'agreeToTr3PersonalTerms': productData.agreeTerms?.agreeToTr3PersonalTerms === true,
                'agreeToTr2PersonalTerms': productData.agreeTerms?.agreeToTr2PersonalTerms === true,
                'agreeToTr2SoleTraderTerms': productData.agreeTerms?.agreeToTr2SoleTraderTerms === true,
                'acceptOptionalNotifications': productData.acceptNotifications === true,
            }
        }
    }
}

function useTrRegistrationForm(): UseTrRegistrationForm {
    const {globalState} = useApplicationContext();
    const isValid = (productData: ProductData) => {
        const isTollAccountValid = isValidTollAccountData(productData);

        if (productData.tollAccountType === TollAccountType.Personal) {
            return isTollAccountValid;
        } else {
            return isTollAccountValid && isValidBusinessDetailsData(productData)
        }
    };

    const isValidBusinessDetailsData = (productData: ProductData) =>
        !!productData.soleTraderDetailData?.abn

    const isValidTollAccountData = (productData: ProductData): boolean =>
        !!productData.tollAccountType &&
        !!productData.tollAccountDetailsPageData?.lpn?.trim() &&
        !!productData.tollAccountDetailsPageData?.tollProvider &&
        !!productData.tollAccountDetailsPageData?.tollAccountNumber?.trim() &&
        !!productData.tollAccountDetailsPageData?.tollAccountEmail?.trim()

    return {
        isValid,
        isValidTollAccountData,
        isValidBusinessDetailsData,
        data: (productData: ProductData): RegistrationForm => {
            return composeRegistrationForm(globalState, productData);
        }
    }
}

export default useTrRegistrationForm;
